// Delivery-log entry detail (task [3.8], ws-e828n) — one delivery record,
// expanded, composed on the shared ItemDetailPage primitive (header + metadata
// grid + optional alert). It reuses the SAME useDeliveryLog() fetcher as the
// list screen (no new network seam): the list already loads the full `records`
// array in memory, so the detail just looks the row up by :id.
//
// message-id / response code / retry count are DERIVED here deterministically
// from the record for display. Body content + insights come from [2.1]'s new
// DeliveryRecord fields, rendered by the ContentTabs panel below the timeline.
import { ArrowLeft, Mail } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router';

import { useDeliveryLog } from '@/api/delivery-log/hooks';
import type { DeliveryRecord, DeliveryStatus } from '@/api/delivery-log/mock';
import {
	ItemDetailPage,
	type ItemDetailAlert,
	type ItemDetailMetadata,
} from '@/components/layout/item-detail-page';
import { PageContainer } from '@/components/layout/page-container';
import { Badge, type BadgeStatus } from '@/components/ui/badge';
import { ContentTabs } from '@/pages/delivery-log/_components/content-tabs';
import { EventTimeline } from '@/pages/delivery-log/_components/event-timeline';

/** Domain delivery status → shared Badge status color (mirrors the list screen). */
const STATUS_TO_BADGE: Record<DeliveryStatus, BadgeStatus> = {
	delivered: 'success',
	failed: 'danger',
	pending: 'neutral',
	bounced: 'warning',
};

/** SMTP-style response line per status — derived, for display only. */
const STATUS_RESPONSE: Record<DeliveryStatus, string> = {
	delivered: '250 2.0.0 OK',
	failed: '550 5.1.1 Recipient rejected',
	bounced: '421 4.7.0 Mailbox unavailable',
	pending: '— queued, not yet attempted',
};

/** Stable small integer from a string — deterministic derived retry count. */
function hashToRange(value: string, max: number): number {
	let h = 0;
	for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) | 0;
	return Math.abs(h) % (max + 1);
}

function derivedRetryCount(record: DeliveryRecord): number {
	if (record.status === 'delivered' || record.status === 'pending') return 0;
	// failed/bounced: 1–3 attempts, deterministic per record.
	return 1 + hashToRange(record.id, 2);
}

function derivedMessageId(record: DeliveryRecord): string {
	return `<${record.id}@mail.email-hub.bsg>`;
}

/** Danger/warning banner for a non-successful delivery, else no alert. */
function buildAlert(
	record: DeliveryRecord,
	retries: number,
): ItemDetailAlert | undefined {
	if (record.status === 'failed') {
		return {
			tone: 'danger',
			message: `This delivery failed after ${retries} ${
				retries === 1 ? 'attempt' : 'attempts'
			}. The provider rejected the recipient address.`,
			action: (
				<Link
					to="/retry-processor"
					viewTransition
					className="inline-flex items-center whitespace-nowrap border border-danger/40 px-2.5 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger/10"
				>
					Open retry processor
				</Link>
			),
		};
	}
	if (record.status === 'bounced') {
		return {
			tone: 'warning',
			message: `The recipient mailbox bounced this message after ${retries} ${
				retries === 1 ? 'attempt' : 'attempts'
			}.`,
		};
	}
	return undefined;
}

function BackToLog() {
	return (
		<Link
			to="/delivery-log"
			viewTransition
			className="inline-flex items-center gap-1.5 border border-border-strong px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
		>
			<ArrowLeft className="size-4" aria-hidden="true" />
			Back to log
		</Link>
	);
}

export default function DeliveryLogEntryPage() {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	// The list hands the clicked row across the navigation so the drill-down is
	// stable in mock mode: getDeliveryLogMock() reseeds a fresh random record set
	// on every fetch, so a background refetch would otherwise reshuffle every id
	// out from under a hook-only lookup. This adds no fetcher — it reuses the row
	// the list already had. The hook lookup below stays the source of truth for a
	// direct/refreshed navigation (no state), and drives loading/error/not-found.
	const passedRecord = (location.state as { record?: DeliveryRecord } | null)
		?.record;
	const { data, isLoading, isError, refetch } = useDeliveryLog();

	const record = passedRecord ?? data?.records.find((r) => r.id === id);

	// Loading — only matters for a direct nav with no passed record and a fetcher
	// still in flight. The mock resolves synchronously; the real fetcher does not.
	if (!record && isLoading) {
		return (
			<PageContainer>
				<div className="h-10 w-64 animate-pulse rounded-md bg-muted" />
				<div className="grid grid-cols-2 gap-4 md:grid-cols-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
					))}
				</div>
				<div className="h-40 animate-pulse rounded-lg bg-muted" />
			</PageContainer>
		);
	}

	if (!record && isError) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<p className="text-sm text-muted-foreground">
						Couldn’t load the delivery log.
					</p>
					<button
						type="button"
						onClick={() => refetch()}
						className="border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
					>
						Try again
					</button>
				</div>
			</PageContainer>
		);
	}

	// Not found — bad/expired :id (or a direct nav where the mock reseeded).
	// Simple recover-forward state, no crash.
	if (!record) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
						<Mail className="size-5" aria-hidden="true" />
					</div>
					<div>
						<h1 className="font-serif text-xl font-bold text-foreground">
							Delivery not found
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">
							No delivery record matches this id. It may have been rotated out
							of the log.
						</p>
					</div>
					<BackToLog />
				</div>
			</PageContainer>
		);
	}

	const retries = derivedRetryCount(record);
	const messageId = derivedMessageId(record);

	const metadata: ItemDetailMetadata[] = [
		{ label: 'Recipient', value: <span className="font-mono">{record.recipient}</span> },
		{
			label: 'Status',
			value: (
				<Badge status={STATUS_TO_BADGE[record.status]}>{record.status}</Badge>
			),
		},
		{ label: 'Provider', value: record.provider },
		{ label: 'Response', value: <span className="font-mono">{STATUS_RESPONSE[record.status]}</span> },
		{ label: 'Retries', value: <span className="tabular-nums">{retries}</span> },
		{
			label: 'Message ID',
			value: <span className="break-all font-mono text-xs">{messageId}</span>,
		},
	];

	return (
		<ItemDetailPage
			icon={<Mail aria-hidden="true" />}
			eyebrow="Delivery Log"
			title={record.subject}
			actions={<BackToLog />}
			metadata={metadata}
			alert={buildAlert(record, retries)}
		>
			<EventTimeline events={record.events} />
			<ContentTabs record={record} />
		</ItemDetailPage>
	);
}
